import {
	observable,
	action,
	runInAction,
	computed,
	reaction,
	IReactionDisposer,
} from "mobx";

import { BetStatus, getCurrentCulture } from "@utils";
import { localizationService, redirect } from "@state";
import { Bet } from "@models";
import { LoaderStore } from "@state/stores/common";

import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import LookupService from "@services/LookupService";
import ModalService from "@services/modals/ModalService";
import type { RootStore } from "@state/stores/types";
import {
	getEventIdsFromMyBets,
	mapBetSlipOffers,
} from "@utils/my-bets/helpers";
import { CashoutStore } from "@state/stores/my-bets/CashoutStore";

import BetSlipApiService from "@api-services/agency/BetSlipApiService";
import { ActiveEventsStore } from "./ActiveEventsStore";
import { ICashoutStoreParent } from "./ICashoutStoreParent";
import { BetCancelStore } from "./BetCancelStore";
import ShareBetSlipStore from "./ShareBetSlipStore";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { findValidSubmitWallet } from "@utils/bet-slip/findValidSubmitWallet";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const MyBetsService = lazy(loadFailPath, ()=>import("@services/my-bets/MyBetsService"));

class MyBetsViewStore implements ICashoutStoreParent {
	rootStore: RootStore;
	activeEventsStore: ActiveEventsStore;
	bet: Bet | null = null;
	betCancelStore: BetCancelStore;
	shareBetSlipStore: ShareBetSlipStore;
	cashoutStore: CashoutStore;
	eventBetsLoader: LoaderStore = new LoaderStore();
	loader: LoaderStore = new LoaderStore();
	pageSize = 10;
	refreshDebounceId: ReturnType<typeof setTimeout> | undefined;

	userLoggedInReaction: IReactionDisposer | null = null;

	@observable openedBetId: string | null;
	@observable isReuseModalOpen = false;
	@observable reuseModalData: {
		id: string;
		betSlipNumber: string;
		isLive: boolean;
	} | null;
	@observable isReuseFormSubmitting = false;
	@observable pageNumber = 1;

	@observable totalItems = 0;
	@observable lastFetchEventId: string | null = null;

	@observable isFirstLoad = true;
	@observable isFetchingData = false;
	@observable bets: Bet[] = [];
	@observable betsPerEvent: EventBetsViewState[] = [];
	/* 	This enables support for LivePage MultiEvent view and allows 
		to display MyBetsTab loading state only for the event
		that is actually being loaded
	*/
	@observable loadingBetsForEventIds: Set<string> = new Set();

	//mini bet filter option type
	@observable public filterOptionType: BetStatus | null = BetStatus.OPEN;

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get isEventBetsLoading() {
		return this.eventBetsLoader.isLoading;
	}

	@computed get isLoadingProcess() {
		return this.loader.isLoadingProcess;
	}

	@computed get betsList(): Bet[] {
		return this.bets;
	}

	@computed get isEmpty() {
		return this.bets.length === 0;
	}

	@computed get openedBet() {
		const idx = this.bets.findIndex((i) => i.id === this.openedBetId);

		if (idx > -1) return this.bets[idx];
		return null;
	}

	@computed get isBetOpen(): boolean {
		return this.openedBet !== null;
	}
	@computed get walletId() {
		return this.rootStore.userAuthStore.activeWalletId;
	}

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.activeEventsStore = new ActiveEventsStore(rootStore);

		this.cashoutStore = new CashoutStore(this);
		this.betCancelStore = new BetCancelStore(rootStore, this);

		this.shareBetSlipStore = new ShareBetSlipStore(rootStore);

		if (this.rootStore.userAuthStore.isLoggedIn) {
			this.loader.suspend();
		}
	}

	@action.bound
	async onInitialize() {
		this.disposeUserLoggedInReaction();
		this.userLoggedInReaction = reaction(
			() => ({
				isLoggedIn:
					this.rootStore.userAuthStore.isLoggedIn &&
					this.rootStore.userAuthStore.userAccounts?.length,
				activeAccountOwner: this.rootStore.userAuthStore.activeWalletId,
			}),
			({ isLoggedIn }) => {
				if (isLoggedIn) {
					if (!this.cashoutStore.isStoreInitialized) {
						this.cashoutStore.onInitialize();
					}
					this.fetchData();
				} else {
					this.onClearData();
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action.bound
	async fetchData() {
		this.loader.suspend();
		try {
			const betStatus: BetStatus | null =
				this.filterOptionType === "all" ? null : this.filterOptionType;

			let walletId =
				findValidSubmitWallet(this.rootStore.userAuthStore)?.id || null;
			const result = await (
				await MyBetsService
			).default.getMiniBets(
				betStatus,
				createApiRequestOptions(RequestContextType.Wallet, walletId)
			);

			this.activeEventsStore.fetchActiveEvents(
				getEventIdsFromMyBets(result),
				this.filterOptionType
			);
			runInAction(() => {
				this.bets = result;
				this.cashoutStore.initializeCashoutSubscription();
			});
		} catch (ex) {
			console.error(ex);
		}

		this.loader.resume();
	}

	@action.bound
	async fetchAdditionalOffers(eventId: string): Promise<void> {
		this.eventBetsLoader.suspend();
		this.isFetchingData = true;
		this.lastFetchEventId = eventId;
		this.loadingBetsForEventIds.add(eventId);
		this.cashoutStore.disposeSubscription(eventId);

		const currentEventBets = this.betsPerEvent.find(
			(item) => item.eventId === eventId
		);

		const filter = {
			eventId: eventId,
			pageSize: this.pageSize,
			page: currentEventBets?.pageNumber || 1,
		};
		let walletId =
			findValidSubmitWallet(this.rootStore.userAuthStore)?.id || null;
		try {
			const response = await BetSlipApiService.findBetsOnAdditionalOffer(
				createApiRequestOptions(RequestContextType.Wallet, walletId),
				filter
			);

			response.item.forEach((betslip) => mapBetSlipOffers(betslip));
			const bets = response.item.map((bet) => new Bet(bet));

			const eventIdsFromBets = getEventIdsFromMyBets(bets);

			const betStatuses = await LookupService.findBetStatuses(
				createApiRequestOptions(
					RequestContextType.Wallet,
					this.rootStore.userAuthStore.activeWalletId
				)
			);

			this.activeEventsStore.fetchActiveEvents(
				eventIdsFromBets,
				betStatuses.find((s) => s.abrv == BetStatus.OPEN)?.abrv
			);

			runInAction(() => {
				const eventBetsIndex = this.betsPerEvent.findIndex(
					(item) => item.eventId === eventId
				);

				if (eventBetsIndex > -1) {
					this.betsPerEvent[eventBetsIndex].bets = bets;
					this.betsPerEvent[eventBetsIndex].totalItems =
						response.totalRecords;
					this.betsPerEvent[eventBetsIndex].pageNumber =
						response.page;
				} else {
					this.betsPerEvent.push({
						eventId: eventId,
						bets: bets,
						pageNumber: 1,
						totalItems: response.totalRecords,
					});
				}

				this.cashoutStore.initializeCashoutSubscription(eventId, bets);
				this.loadingBetsForEventIds.delete(eventId);
				if (this.loadingBetsForEventIds.size === 0) {
					this.eventBetsLoader.resume();
				}
				this.isFetchingData = false;
			});
		} catch (error) {
			runInAction(() => {
				console.error(error);
				const errorMessage = localizationService.t("ERROR.TITLE");
				this.rootStore.notificationStore.error(errorMessage);

				const eventBetsIndex = this.betsPerEvent.findIndex(
					(item) => item.eventId === eventId
				);

				if (eventBetsIndex > -1) {
					const eventBetsIndex = this.betsPerEvent.findIndex(
						(item) => item.eventId === eventId
					);
					this.betsPerEvent[eventBetsIndex].bets = [];
					this.betsPerEvent[eventBetsIndex].pageNumber = 1;
				}

				this.loadingBetsForEventIds.delete(eventId);
				if (this.loadingBetsForEventIds.size === 0) {
					this.eventBetsLoader.resume();
				}
				this.isFetchingData = false;
			});
		}
	}

	@action.bound
	async filterMiniBets(type: BetStatus | null) {
		this.filterOptionType = type;
		this.fetchData();
	}

	@action.bound
	async refresh() {
		clearTimeout(this.refreshDebounceId);
		this.refreshDebounceId = setTimeout(this.fetchData, 500);
	}

	@action.bound
	async reuseBetSlip() {
		this.rootStore.betSlipStore.useBetSlip(this.bet, undefined, {
			trackAsReuse: true,
		});

		if (this.bet?.isLive) {
			redirect(`/${getCurrentCulture()}/${DefaultPath.LIVE}`);
		} else {
			redirect(`/${getCurrentCulture()}/${DefaultPath.PREMATCH}`);
		}

		this.closeReuseBetSlipModal();
	}

	@action.bound
	onOpen(betId: string) {
		this.openedBetId = betId;
	}

	@action.bound
	onClose() {
		this.openedBetId = null;
	}

	@action.bound
	openReuseBetSlipModal(bet: Bet) {
		this.isReuseModalOpen = true;
		this.reuseModalData = {
			betSlipNumber: bet.betSlipNumber,
			id: bet.id,
			isLive: bet.isLive,
		};
		this.bet = bet;
		ModalService.onOpen();
	}

	@action.bound
	closeReuseBetSlipModal() {
		this.isReuseModalOpen = false;
		this.reuseModalData = null;
		this.bet = null;
	}

	@action.bound
	setPageNumber(nmbr: number) {
		if (nmbr !== this.pageNumber) {
			this.pageNumber = nmbr;
		}
		if (this.lastFetchEventId) {
			this.fetchAdditionalOffers(this.lastFetchEventId);
		}
	}

	@action.bound
	setEventPageNumber(eventId: string, nmbr: number) {
		const eventBetsIndex = this.betsPerEvent.findIndex(
			(ebComb) => ebComb.eventId === eventId
		);

		if (eventBetsIndex > -1) {
			if (this.betsPerEvent[eventBetsIndex].pageNumber != nmbr) {
				this.betsPerEvent[eventBetsIndex].pageNumber = nmbr;

				this.fetchAdditionalOffers(eventId);
			}
		}
	}

	syncEventBetsTab(eventId: string) {
		if (
			this.betsPerEvent.some(
				(eventWithOpenMyBetsTab) =>
					eventWithOpenMyBetsTab.eventId === eventId
			)
		) {
			this.fetchAdditionalOffers(eventId);
		}
	}

	@action.bound
	deleteEventBets(eventId: string) {
		const eventBetsIndex = this.betsPerEvent.findIndex(
			(ebComb) => ebComb.eventId === eventId
		);

		if (eventBetsIndex > -1) {
			this.betsPerEvent.splice(eventBetsIndex, 1);
		}
	}

	@action.bound
	onEventBetsDispose(eventId: string) {
		this.deleteEventBets(eventId);
		this.cashoutStore.disposeSubscription(eventId);

		this.lastFetchEventId = null;
		this.pageNumber = 1;
		this.totalItems = 0;
	}

	@action.bound
	onClearData() {
		this.bets = [];
		this.betsPerEvent = [];
		this.filterOptionType = BetStatus.OPEN;
		this.bet = null;
		this.loader.resume();
		this.cashoutStore.onDispose();
		this.closeReuseBetSlipModal();
	}

	@action.bound
	onDispose() {
		this.disposeUserLoggedInReaction();
		this.cashoutStore.onDispose();
		this.activeEventsStore.onDispose();
		this.shareBetSlipStore.onDispose();
	}

	@action.bound
	disposeUserLoggedInReaction() {
		if (this.userLoggedInReaction != null) {
			this.userLoggedInReaction();
			this.userLoggedInReaction = null;
		}
	}
}

export default MyBetsViewStore;

export type EventBetsViewState = {
	eventId: string;
	bets: Bet[];
	pageNumber: number;
	totalItems: number;
};
